import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { experiences } from '../../data/portfolio';

const Section = styled.section`
  padding: 80px 0 80px 48px;

  @media (max-width: 1080px) {
    padding: 80px 24px;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 48px 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const typeColor: Record<string, string> = {
  current: '#22c55e',
  research: '#a78bfa',
  teaching: '#60a5fa',
  leadership: '#f59e0b',
  development: '#64748b',
};

const Card = styled(motion.div)<{ $type: string }>`
  position: relative;
  padding: 20px 20px 20px 24px;
  border-radius: 10px;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  cursor: default;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 20px;
    bottom: 20px;
    width: 3px;
    border-radius: 3px;
    background: ${({ $type }) => typeColor[$type] ?? '#64748b'};
    opacity: 0.5;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: var(--clr-hover-bg);
    border-color: var(--clr-accent-border);

    &::before {
      opacity: 1;
    }
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const JobTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 3px 0;
  line-height: 1.3;
`;

const CompanyLink = styled.a`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.accent};
  text-decoration: none;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.75;
  }
`;

const CompanyText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.accent};
`;

const Duration = styled.span`
  font-size: 11px;
  color: var(--clr-text-faint);
  white-space: nowrap;
  flex-shrink: 0;
  padding-top: 2px;
  letter-spacing: 0.3px;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`;

const TypeBadge = styled.span<{ $type: string }>`
  font-size: 10px;
  font-weight: 600;
  color: ${({ $type }) => typeColor[$type] ?? '#64748b'};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  background: ${({ $type }) => (typeColor[$type] ?? '#64748b') + '18'};
  border: 1px solid ${({ $type }) => (typeColor[$type] ?? '#64748b') + '40'};
  padding: 2px 8px;
  border-radius: 12px;
`;

const Location = styled.span`
  font-size: 11px;
  color: var(--clr-text-faint);
`;

const Description = styled.p`
  font-size: 13px;
  color: var(--clr-text-dim);
  line-height: 1.6;
  margin: 0 0 10px 0;
`;

const Highlights = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 12px 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const HighlightItem = styled.li`
  font-size: 12.5px;
  color: var(--clr-text-dim);
  line-height: 1.5;
  padding-left: 14px;
  position: relative;

  &::before {
    content: '\25B8';
    position: absolute;
    left: 0;
    color: var(--clr-accent-mid);
    font-size: 10px;
    top: 1px;
  }
`;

const SkillTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: var(--clr-accent-soft);
  background: var(--clr-accent-fill);
  border: 1px solid var(--clr-accent-border);
  padding: 3px 10px;
  border-radius: 20px;
  letter-spacing: 0.2px;
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

const Experience: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Section id="experience">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <SectionTitle>{t('experience.sectionTitle')}</SectionTitle>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
      >
        <List>
          {experiences.map((exp) => (
            <Card
                key={exp.id}
                variants={itemVariants}
                transition={{ duration: 0.4 }}
                $type={exp.type}
              >
              <CardTop>
                <TitleBlock>
                  <JobTitle>{exp.title}</JobTitle>
                  {exp.links && exp.links.length > 0 ? (
                    <CompanyLink
                      href={exp.links[0].url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {exp.company} &#x2197;
                    </CompanyLink>
                  ) : (
                    <CompanyText>{exp.company}</CompanyText>
                  )}
                </TitleBlock>
                <Duration>{exp.duration}</Duration>
              </CardTop>

              <Meta>
                <TypeBadge $type={exp.type}>{t(`experience.types.${exp.type}`, exp.type)}</TypeBadge>
                <Location>{exp.location}</Location>
              </Meta>

              <Description>{t(`experience.entries.${exp.id}.description`, exp.description)}</Description>

              {exp.highlights && exp.highlights.length > 0 && (
                <Highlights>
                  {(t(`experience.entries.${exp.id}.highlights`, { returnObjects: true, defaultValue: exp.highlights }) as string[])
                    .slice(0, 3)
                    .map((h, i) => (
                      <HighlightItem key={i}>{h}</HighlightItem>
                    ))}
                </Highlights>
              )}

              {exp.skills && exp.skills.length > 0 && (
                <SkillTags>
                  {exp.skills.map((skill) => (
                    <Tag key={skill}>{skill}</Tag>
                  ))}
                </SkillTags>
              )}
            </Card>
          ))}
        </List>
      </motion.div>
    </Section>
  );
};

export default Experience;
